import BodyText from 'LuxKit/Typography/BodyText'
import type { ComponentProps, KeyboardEventHandler, MouseEventHandler } from 'react'
import React, { forwardRef, useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import FormatCurrency from './FormatCurrency'
import Caption from 'LuxKit/Typography/Caption'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import type { SvgIconProps } from 'LuxKit/SvgIcon'
import Clickable from './Clickable'
import LoadingIndicator from './Loading/LoadingIndicator'
import PriceRowSale from 'LuxKit/PricePoints/PriceRowSale'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import LineTagAltIcon from 'LuxKit/Icons/line/LineTagAltIcon'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Label from 'LuxKit/Label'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'

const StyledIconButton = styled(IconButton)`
    transform: translateY(-${rem(4)}) translateX(${rem(4)});
`

const ContentGroup = styled(Group)`
  flex-grow: 1;
`

const Sheet = styled(Group)`
  padding: ${rem(20)};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
`

const Loader = styled(LoadingIndicator)`
  padding: 0;
`

const AccessibleClickableDiv = styled.div`
  cursor: pointer;
  
  &:focus {
    ${interactiveFocusStyle}
  }
  
  &:focus:not(:focus-visible) {
    outline: none;
  }
`

interface Props {
  onClick?: () => void
  title: string
  IconComponent: React.ComponentType<SvgIconProps>
  price?: number
  priceUnit?: string
  isPriceLoading?: boolean
  to?: string
  currencyFormat?: ComponentProps<typeof FormatCurrency>['format']
  luxPlusPrice?: number
  className?: string
  tileType: 'car_hire' | 'flight' | 'transfer' | 'insurance' | 'cfmr' | 'tour'
  onDismissClick?: () => void
  discountPercentage?: number
  hasLoyaltyBonusPoints?: boolean
  alertText?: string
  onAlertClick?: () => void
}

const CompactOrderUpsellTile = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    title,
    IconComponent,
    to,
    onClick,
    price,
    priceUnit,
    currencyFormat = 'roundedDollar',
    isPriceLoading,
    luxPlusPrice,
    className,
    tileType,
    onDismissClick,
    discountPercentage,
    hasLoyaltyBonusPoints,
    alertText,
    onAlertClick: alertClickCallback,
  } = props

  const showLuxPlusPricing = !!luxPlusPrice && priceUnit && !isPriceLoading
  const showPricing = !!price && priceUnit && !showLuxPlusPricing && !isPriceLoading
  const isCarHireTile = tileType === 'car_hire'
  const isFlightTile = tileType === 'flight'
  const isInsuranceBookingProtection = tileType === 'cfmr'

  const handleDismissClick = useCallback<MouseEventHandler<HTMLButtonElement>>(event => {
    event.preventDefault()
    onDismissClick?.()
  }, [onDismissClick])

  const handleAlertClick = useCallback<MouseEventHandler<HTMLDivElement>>((e) => {
    if (!alertClickCallback) return
    e.preventDefault()
    e.stopPropagation()
    alertClickCallback()
  }, [alertClickCallback])

  const handleAlertKeyDown = useCallback<KeyboardEventHandler<HTMLDivElement>>((e) => {
    if (!alertClickCallback) return
    // Enter activates on keyDown (standard button behavior)
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      alertClickCallback()
    }
    // Prevent Space from scrolling the page
    if (e.key === ' ') {
      e.preventDefault()
    }
  }, [alertClickCallback])

  const handleAlertKeyUp = useCallback<KeyboardEventHandler<HTMLDivElement>>((e) => {
    if (!alertClickCallback) return
    // Space activates on keyUp (matches onClick behavior and allows cancellation)
    if (e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      alertClickCallback()
    }
  }, [alertClickCallback])

  const tier = useAppSelector(getLoyaltyDisplayTier)

  return <Clickable ref={ref} to={to} preventSwipeClick onClick={onClick} className={className}>
    <Sheet direction="vertical" tabletDirection="horizontal-reverse" gap={4} fullHeight fullWidth>
      <Group direction="horizontal" horizontalAlign="space-between" tabletDirection="vertical-reverse" tabletHorizontalAlign="end" tabletVerticalAlign="space-between" gap={8}>
        <IconComponent colour="highlight-primary-light-contrast" size="L" />
        <StyledIconButton kind="tertiary" onClick={handleDismissClick} size="small">
          <LineTimesIcon />
        </StyledIconButton>
      </Group>
      <ContentGroup direction="vertical" verticalAlign="space-between" gap={32}>
        <Group direction="vertical" desktopDirection="horizontal" gap={2} desktopGap={8} verticalAlign="center">
          <Heading variant="heading6">
            {title}
          </Heading>
          {(discountPercentage || hasLoyaltyBonusPoints) && <LabelGroup>
            {discountPercentage && <PromotionLabel icon={<LineTagAltIcon />} variant="default">{discountPercentage}% off</PromotionLabel>}
            {hasLoyaltyBonusPoints && <LoyaltyTierPill kind="primary" tier={tier}>Bonus points</LoyaltyTierPill>}
          </LabelGroup>}
        </Group>
        <div>
          {isPriceLoading && <Loader inline ballSize={8} />}
          {isCarHireTile && !showPricing && !showLuxPlusPricing && <Group direction="vertical">
            <BodyTextBlock variant="small" startIcon={<LineCheckIcon colour="highlight-primary-light-contrast" size="S" />}>
              Free cancellation available
            </BodyTextBlock>
            <BodyTextBlock variant="small" startIcon={<LineCheckIcon colour="highlight-primary-light-contrast" size="S" />}>
              24/7 support
            </BodyTextBlock>
          </Group>}
          {isFlightTile && !showPricing && !showLuxPlusPricing && <Group direction="vertical">
            <BodyTextBlock variant="small" startIcon={<LineCheckIcon colour="highlight-primary-light-contrast" size="S" />}>
              Great fares from major airlines
            </BodyTextBlock>
            <BodyTextBlock variant="small" startIcon={<LineCheckIcon colour="highlight-primary-light-contrast" size="S" />}>
              24/7 support
            </BodyTextBlock>
          </Group>}
          {!isPriceLoading && alertText && (
            <AccessibleClickableDiv
              role="button"
              tabIndex={0}
              onClick={handleAlertClick}
              onKeyDown={handleAlertKeyDown}
              onKeyUp={handleAlertKeyUp}
              aria-label={`${alertText}. Click for more information`}
            >
              <Label kind="plain" variant="urgency" endIcon={<LineInfoCircleIcon />}>
                {alertText}
              </Label>
            </AccessibleClickableDiv>
          )}
          {showPricing &&
            <div>
              {!isInsuranceBookingProtection && (
                <BodyText variant="medium">
                  From
                </BodyText>
              )}
              <BodyText variant="medium">
                <b><FormatCurrency value={price} format={currencyFormat} /></b> /{priceUnit}
              </BodyText>
            </div>}
          {showLuxPlusPricing &&
            <div>
              <BodyText variant="medium">
                From
              </BodyText>
              <div>
                <PriceRowLuxPlusPrice
                  memberPrice={luxPlusPrice}
                  size="M"
                  saleUnit={priceUnit}
                  priceFormat="dynamicDollar"
                />
                <div>
                  <PriceRowSale value={price ?? 0} priceFormat="dynamicDollar" />
                  <Caption
                    as="s"
                    variant="large"
                    colour="neutral-two"
                  >
                    {' '}/{priceUnit}
                  </Caption>
                </div>
              </div>
            </div>}
        </div>
      </ContentGroup>
    </Sheet>
  </Clickable>
})

export default CompactOrderUpsellTile
